import { RateLimit } from 'record-it-utils/utils/RateLimit/RateLimit';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';
import { RelativeSynchronizationEntityIdsByEntityName } from './EntityActualization';
import { StorageAdapter } from '../StorageAdapter';

export class EntityActualizationInfo<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint
> {
  private readonly actualizationInfoStorageKey =
    'EntityActualizationInfo::actualizationInfo';

  private lastSuccessfulActualizationInfo: LastActualizationInfo<TSynchronizationEnvironmentTypes> | null =
    null;

  private initialized: boolean = false;

  private saveActualizationInfoRateLimited = RateLimit.rateLimitFunction(
    this.saveActualizationInfo.bind(this),
    50
  );

  public async init(): Promise<void> {
    await this.loadActualizationInfo();

    this.initialized = true;
  }

  public async flush(): Promise<void> {
    if (this.saveActualizationInfoRateLimited.isPending()) {
      this.saveActualizationInfoRateLimited.cancel();
      await this.saveActualizationInfo();
    }
  }

  public destroy(): void {
    this.initialized = false;
    this.saveActualizationInfoRateLimited.cancel();
  }

  public actualizationFinishedSuccessfully({
    relativeSynchronizationEntityIdsByEntityName
  }: {
    relativeSynchronizationEntityIdsByEntityName: RelativeSynchronizationEntityIdsByEntityName<TSynchronizationEnvironmentTypes>;
  }): void {
    this.assertIsInitialized();
    this.lastSuccessfulActualizationInfo = {
      relativeSynchronizationEntityIdsByEntityName
    };

    this.saveActualizationInfoRateLimited();
  }

  public getLastSuccessfulActualizationInfo(): LastActualizationInfo<TSynchronizationEnvironmentTypes> | null {
    this.assertIsInitialized();

    return this.lastSuccessfulActualizationInfo;
  }

  private async loadActualizationInfo(): Promise<void> {
    const storedData:
      | StoredActualizationInfo<TSynchronizationEnvironmentTypes>
      | null
      | undefined = await StorageAdapter.getGeneralItem(
      this.actualizationInfoStorageKey
    );

    if (storedData) {
      this.lastSuccessfulActualizationInfo =
        storedData.lastSuccessfulActualizationInfo
          ? {
              relativeSynchronizationEntityIdsByEntityName: new Map(
                Object.entries(
                  storedData.lastSuccessfulActualizationInfo
                    .relativeSynchronizationEntityIdsByEntityName
                ).map(([entityName, ids]) => {
                  return [
                    entityName as TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
                    new Set(ids as Array<string>)
                  ];
                })
              )
            }
          : null;
    } else {
      this.lastSuccessfulActualizationInfo = null;
    }
  }

  private async saveActualizationInfo(): Promise<void> {
    this.assertIsInitialized();

    const toStore: StoredActualizationInfo<TSynchronizationEnvironmentTypes> = {
      lastSuccessfulActualizationInfo: this.lastSuccessfulActualizationInfo
        ? {
            relativeSynchronizationEntityIdsByEntityName: Object.fromEntries(
              Array.from(
                this.lastSuccessfulActualizationInfo.relativeSynchronizationEntityIdsByEntityName.entries()
              ).map(([entityName, entityIds]) => {
                return [entityName, Array.from(entityIds.values())];
              })
            ) as Partial<
              Record<
                TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
                Array<string>
              >
            >
          }
        : null
    };

    await StorageAdapter.setGeneralItem(
      this.actualizationInfoStorageKey,
      toStore
    );
  }

  private assertIsInitialized(): void {
    if (!this.initialized) {
      throw new Error(
        "can't use the EntityActualizationInfo because it isn't initialized"
      );
    }
  }
}

/**
 * General info about the last finished actualization.
 */
export type LastActualizationInfo<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint
> = {
  /**
   * Contains all the entity ids which were considered in the actualization.
   */
  relativeSynchronizationEntityIdsByEntityName: RelativeSynchronizationEntityIdsByEntityName<TSynchronizationEnvironmentTypes>;
};

type StoredActualizationInfo<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint
> = {
  lastSuccessfulActualizationInfo: {
    relativeSynchronizationEntityIdsByEntityName: Partial<
      Record<
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
        Array<string>
      >
    >;
  } | null;
};
